import React, { useEffect, useState } from 'react';
import './account-payment.scss';
import { useDispatch, useSelector } from 'react-redux';
import { selectAccountPaymentPageContext } from 'selectors/accountPaymentSelectors';
import { makePaymentRequest } from '../../actions/accountActions';
import { currencyFormat } from '../../utilities/core-utilities';
import { Form, FormValidationMode, Icon, Anchor } from '@ampol/design-system';
import { setAccountPaymentButton } from 'actions/modalMetaActions';
import * as yup from 'yup';
import Alert from '../_global/alert/alert';
import Env from '../../Env';
import PaymentCode from 'constants/payment-code';
import Regex from 'constants/regex';
import Validation from 'constants/validation-messages';
import SiteLoader from 'components/page-loader/page-loader';
import { trackCustomEvent } from 'utilities/app-insights';

type AccountPaymentProps = {
  accountNumber: string;
  accountBalance: number;
  removePaymentButtonCallback: () => void;
  isUpdatePaymentsPage?: boolean;
};

type AccountPaymentFormValues = {
  amount: string;
};

const paymentTypeOptions: Record<string, string> = {
  [`${PaymentCode.DEFAULT_PAYMENT}`]: 'EFT',
  [`${PaymentCode.CREDIT_CARD_PAYMENT}`]: 'Direct Debit - Credit Card',
  [`${PaymentCode.BANK_ACCOUNT_PAYMENT}`]: 'Direct Debit - Bank Account',
};

const ACCOUNT_PAYMENT_FORM_ID = 'account-payment';

const AccountPayment: React.FC<AccountPaymentProps> = ({
  accountNumber,
  accountBalance,
  removePaymentButtonCallback,
  isUpdatePaymentsPage = false,
}) => {
  const dispatch = useDispatch();

  const {
    assignedGrandfatherClientNumber,
    readOnly,
    primaryActionButton,
    loggedInUser,
    assignedAccountNumber,
    paymentType,
    paymentTypeLoading,
    paymentTypeLoaded,
    makePaymentLoading,
  } = useSelector(selectAccountPaymentPageContext);

  const currentPaymentTypeCode = paymentType?.paymentTypeCode;
  const isPayNowAllowed = !!paymentType?.allowPayment;

  const [defaultValues, setDefaultValues] = useState<AccountPaymentFormValues>({
    amount: '',
  });

  const getPayNowButtonConfig = (inactive: boolean) => ({
    ...primaryActionButton,
    label: 'Pay now',
    formId: ACCOUNT_PAYMENT_FORM_ID,
    inactive,
    type: readOnly ? 'button' : 'submit',
  });

  useEffect(() => {
    !isUpdatePaymentsPage &&
      !paymentType?.allowPayment &&
      dispatch(
        setAccountPaymentButton({
          ...primaryActionButton,
          label: 'Make a Payment',
          iconSuffix: <Icon url='/icons/external-link.svg' size={18} className='button-icon' />,
          type: 'button',
          inactive: readOnly,
          onClick: () => {
            trackCustomEvent(
              'Click Make a Payment',
              loggedInUser,
              assignedAccountNumber,
              'Clicked Make a Payment button to redirect to Westpac.',
              { selectedAccount: accountNumber }
            );
            !readOnly && window.open(Env.getPaymentCardPaymentUrl());
          },
        })
      );
    !isUpdatePaymentsPage &&
      isPayNowAllowed &&
      dispatch(setAccountPaymentButton(getPayNowButtonConfig(readOnly || accountBalance <= 0)));
    return () => {
      removePaymentButtonCallback();
    };
  }, [isUpdatePaymentsPage]);

  const paymentSchema = yup.object().shape({
    amount: yup
      .string()
      .required(Validation.AMOUNT_REQUIRED)
      .matches(Regex.CURRENCY, Validation.CURRENCY)
      .test('min', Validation.MIN_CURRENCY_ONE, (value) => {
        if (!value || !Regex.CURRENCY.test(value)) return true; // Allow empty strings
        const numericValue = parseFloat(value);
        return numericValue >= 1;
      })
      .test('max', `Amount cannot exceed your balance ${currencyFormat(accountBalance)}.`, (value) => {
        if (!value || !Regex.CURRENCY.test(value)) return true; // Allow empty strings
        const numericValue = parseFloat(value);
        return numericValue <= accountBalance;
      }),
  });

  const handleChange = (values: AccountPaymentFormValues, name: string | undefined) => {
    if (name !== 'amount') return;
    setDefaultValues({
      amount: values.amount,
    });
    window.dataLayer.push({
      event: 'Card_Portal_Accounts_Make_A_Payment_Direct_Debit_Amount',
      dollar_amount: values.amount,
    });
  };

  const handleSubmit = (values: AccountPaymentFormValues) => {
    const { amount } = values;

    dispatch(makePaymentRequest(assignedGrandfatherClientNumber, accountNumber, parseFloat(amount)));

    setDefaultValues({ amount: '' });

    trackCustomEvent(
      'Submit Pay Now',
      loggedInUser,
      assignedAccountNumber,
      'Clicked Pay Now button to submit Account Payment.',
      { selectedAccount: accountNumber }
    );
  };

  const handleStateChange = ({ isValid }: { isValid: boolean }) => {
    const balance = Math.floor(accountBalance);

    !isUpdatePaymentsPage &&
      isPayNowAllowed &&
      dispatch(setAccountPaymentButton(getPayNowButtonConfig(readOnly || !isValid || balance <= 0)));
  };

  return (
    <div className={'account-payment'}>
      {makePaymentLoading && <SiteLoader />}
      {paymentTypeLoading && <div>Loading...</div>}
      {paymentTypeLoaded && (
        <>
          <h4>Current payment method:</h4>
          <div className={'account-payment__current'}>{paymentTypeOptions[currentPaymentTypeCode]}</div>

          <Form
            schema={paymentSchema}
            defaultValues={defaultValues}
            onSubmit={handleSubmit}
            onStateChange={handleStateChange}
            onChange={handleChange}
            validationMode={FormValidationMode.onChange}
            id={ACCOUNT_PAYMENT_FORM_ID}
          >
            {isUpdatePaymentsPage && (
              <div>
                <h4>Payment methods available:</h4>
                <div className='account-payment__payment-methods'>
                  {paymentTypeOptions[PaymentCode.CREDIT_CARD_PAYMENT]}
                  <Anchor
                    href={Env.getPaymentRegisterCardUrl()}
                    className={'account-payment__icon-text'}
                    onClick={() => {
                      window.dataLayer.push({
                        event: 'Card_Portal_Accounts_Update_Payment_Details_Direct_Debit_Credit_Card',
                        selected: true,
                      });
                      trackCustomEvent(
                        'Click Credit Card Link',
                        loggedInUser,
                        assignedAccountNumber,
                        'Clicked the link to redirect to Westpac website.',
                        { selectedAccount: accountNumber }
                      );
                    }}
                  >
                    <Icon url='/icons/external-link.svg' />
                    You will be redirected to Westpac to enter card details.
                  </Anchor>
                  {paymentTypeOptions[PaymentCode.BANK_ACCOUNT_PAYMENT]}
                  <Anchor
                    href={Env.getPaymentRegisterDDAuthUrl()}
                    className={'account-payment__icon-text'}
                    onClick={() => {
                      window.dataLayer.push({
                        event: 'Card_Portal_Accounts_Update_Payment_Details_Direct_Debit_Bank_Account',
                        selected: true,
                      });
                      trackCustomEvent(
                        'Click Bank Account Link',
                        loggedInUser,
                        assignedAccountNumber,
                        'Click the link to redirect to complete Direct Debit form.',
                        { selectedAccount: accountNumber }
                      );
                    }}
                  >
                    <Icon url='/icons/download.svg' />
                    You will be redirected to complete a Direct Debit form.
                  </Anchor>
                </div>
              </div>
            )}

            {!isUpdatePaymentsPage && (
              <>
                <div className={'account-payment__balance'}>
                  Today&apos;s Balance:{' '}
                  {currentPaymentTypeCode === PaymentCode.BANK_ACCOUNT_PAYMENT && makePaymentLoading
                    ? 'Loading'
                    : `${currencyFormat(accountBalance)}`}
                </div>
                {!paymentType?.allowPayment && (
                  <>
                    <div className='account-payment__alert py-2'>
                      <Alert dismissible={false} onClose={() => {}}>
                        <Icon className='account-payment__alert-icon' url='/icons/alert.svg' />
                        Your account is currently suspended, and direct debit payment through this service is
                        unavailable.
                      </Alert>
                    </div>
                    <div>
                      <p className={'account-payment__icon-text'}>
                        <Icon url='/icons/external-link.svg' />
                        <span>
                          You can choose to use your credit card to process a payment by clicking on the Make a Payment
                          button below. Please note you will be redirected to Westpac.
                          <br /> <br />
                          Alternatively, please contact the Ampol Accounts Receivable Team on{' '}
                          <a href={'tel:1300138469'}>1300 138 469</a>, Monday to Friday, 8:30am to 5:00pm AEST/AEDST.
                        </span>
                      </p>
                    </div>
                  </>
                )}
                {paymentType?.allowPayment && (
                  <div className={'account-payment__amount'}>
                    <Form.Input
                      label={'Direct Debit Amount'}
                      name={'amount'}
                      inputPrefix={'$'}
                      forceFloating={true}
                      disabled={
                        readOnly || (currentPaymentTypeCode === PaymentCode.BANK_ACCOUNT_PAYMENT && makePaymentLoading)
                      }
                    />
                  </div>
                )}
              </>
            )}
          </Form>

          {isUpdatePaymentsPage && currentPaymentTypeCode === PaymentCode.DEFAULT_PAYMENT && (
            <div className='account-payment__alert'>
              <Alert dismissible={true} onClose={() => {}}>
                <Icon className='account-payment__alert-icon' url='/icons/alert.svg' />
                Changing from EFT/cheque to direct debit is an irreversible action.
              </Alert>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AccountPayment;
